import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import { fetchReviewBoard } from '../../lib/consoleReviews';

// ── "Reviews" ──────────────────────────────────────────────────────────────
//
// One place for everything waiting on an approval, so approving is a thing you
// go and do rather than four screens you remember to visit. It replaces nothing
// and absorbs no screen: each tile is a door to the tool that already does the
// work, counted here so the door tells you whether to open it.
//
// The middle tile is the honest one. Factory products have no review state in
// the schema, so there is no "waiting" number to show and this does not pretend
// otherwise — see the note in consoleReviews.js.

const relTime = (iso, isAr) => {
  if (!iso) return '—';
  const h = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
  if (h < 1) return isAr ? 'أقل من ساعة' : 'under an hour';
  if (h < 24) return isAr ? `${h} ساعة` : `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return isAr ? (d === 1 ? 'يوم' : `${d} يوم`) : `${d}d`;
  return isAr ? `${Math.floor(d / 30)} شهر` : `${Math.floor(d / 30)}mo`;
};

export default function ConsoleReviews({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    fetchReviewBoard()
      .then((x) => { if (alive) setD(x); })
      .catch((e) => {
        if (!alive) return;
        console.error('[ConsoleReviews] load failed:', e?.message || e);
        setErr(e?.message || 'load failed');
      });
    return () => { alive = false; };
  }, []);

  const oldestFile = d?.files?.oldest || null;
  const fileName = oldestFile
    ? (oldestFile.company_name || oldestFile.company_name_latin || oldestFile.full_name || (isAr ? 'مورّد' : 'a supplier'))
    : null;

  const TILES = d ? [
    {
      k: 'files',
      ar: 'ملفات الموردين', en: 'Supplier files',
      n: d.files.count,
      subAr: d.files.oldest ? `أقدم ملف ينتظر منذ ${relTime(d.files.oldest.created_at, true)}` : 'لا شيء ينتظر',
      subEn: d.files.oldest ? `Oldest waiting ${relTime(d.files.oldest.created_at, false)}` : 'Nothing waiting',
      go: d.files.oldest ? `/admin2/verification/${d.files.oldest.id}` : '/admin2/suppliers?tab=registered',
      tone: d.files.count > 0 ? 'warn' : 'ok',
    },
    {
      k: 'catalogs',
      ar: 'الكتالوجات المستوردة', en: 'Imported catalogs',
      n: d.catalogs.count,
      subAr: d.catalogs.count ? `من ${d.catalogs.factories} مصنعًا · انتهى استخراجها` : 'لا شيء ينتظر التدقيق',
      subEn: d.catalogs.count ? `from ${d.catalogs.factories} factories · extraction done` : 'Nothing to check',
      go: '/admin2/import',
      tone: d.catalogs.count > 0 ? 'warn' : 'ok',
    },
    {
      k: 'products',
      ar: 'منتجات المصانع', en: 'Factory products',
      n: d.products.total,
      // Not a waiting count, and it does not claim to be.
      subAr: 'إجمالي الكتالوج — تدقيق يدوي، لا طابور',
      subEn: 'Catalog total — manual QA, not a queue',
      go: '/admin2/catalog-qa',
      tone: 'plain',
    },
    {
      k: 'violations',
      ar: 'مخالفات التواصل', en: 'Contact violations',
      n: d.violations.count == null ? '—' : d.violations.count,
      subAr: d.violations.count == null ? 'تعذّرت القراءة' : d.violations.count ? 'أرقام وحسابات أُخفيت — بانتظار قرارك' : 'لا شيء ينتظر',
      subEn: d.violations.count == null ? 'Could not load' : d.violations.count ? 'Masked contact details — awaiting your call' : 'Nothing waiting',
      go: '/admin2/contact-violations',
      tone: d.violations.count > 0 ? 'warn' : 'ok',
    },
  ] : [];

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="reviews">
      <div className="ac-page" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
        <div className="ac-page-head">
          <div>
            <h1 className="ac-h1">{isAr ? 'المراجعات' : 'Reviews'}</h1>
            <p className="ac-sub">
              {isAr ? 'كل ما ينتظر اعتمادك، في مكان واحد.' : 'Everything waiting for your approval, in one place.'}
            </p>
          </div>
        </div>

        {err && <p style={{ color: 'var(--ac-danger)', fontSize: 13 }}>{err}</p>}

        <div className="ac-signups" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))' }}>
          {!d
            ? Array.from({ length: 4 }).map((_, i) => <div key={i} className="ac-skel" style={{ height: 118 }} />)
            : TILES.map((t) => (
              <button key={t.k} type="button" className="ac-signup" onClick={() => nav(t.go)}>
                <p className="ac-signup-lbl">{isAr ? t.ar : t.en}</p>
                <span className="ac-signup-num">
                  <b style={{ color: t.tone === 'warn' ? 'var(--ac-warn)' : undefined }}>{t.n}</b>
                </span>
                <p className="ac-signup-foot">{isAr ? t.subAr : t.subEn}</p>
              </button>
            ))}
        </div>

        {d && d.files.count > 0 && (
          <div className="ac-card" style={{ marginTop: 22, padding: 22, textAlign: 'center' }}>
            <h2 className="ac-h1" style={{ fontSize: 18, marginBottom: 6 }}>{isAr ? 'ابدأ بالأقدم' : 'Start with the oldest'}</h2>
            <p className="ac-sub" style={{ marginBottom: 14 }}>
              {isAr
                ? `ملف ${fileName} ينتظر منذ ${relTime(oldestFile.created_at, true)}.`
                : `${fileName}'s file has been waiting ${relTime(oldestFile.created_at, false)}.`}
            </p>
            <button className="ac-btn ac-btn-primary" onClick={() => nav(`/admin2/verification/${oldestFile.id}`)}>
              {isAr ? 'مراجعة الملف الآن' : 'Review it now'}
            </button>
          </div>
        )}

        {d && d.files.count === 0 && d.catalogs.count === 0 && !d.violations.count && (
          <div className="ac-empty" style={{ marginTop: 20 }}>
            {isAr ? 'لا شيء ينتظر اعتمادك 👌' : 'Nothing is waiting for your approval 👌'}
          </div>
        )}
      </div>
    </ConsoleShell>
  );
}
